"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Row, Col, Typography, Spin, Tabs, Descriptions, Collapse, InputNumber, Button, message, Divider, Skeleton } from "antd"
import { ShoppingCartOutlined, TeamOutlined } from "@ant-design/icons"
import { getProduct, getRelatedProducts } from "../services/ProductServices"
import { useAuth } from "../contexts/authContext"
import { QuoteRequestModal } from "../components/modals/QuoteRequestModal"
import "../assets/styles/product-detail.css"

const { Title, Text } = Typography
const { TabPane } = Tabs
const { Panel } = Collapse

const ProductDetail = () => {
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const { productId } = useParams()
  const navigate = useNavigate()
  const { userLoggedIn, getToken, user } = useAuth()
  const [relatedProducts, setRelatedProducts] = useState([])
  const [relatedLoading, setRelatedLoading] = useState(false)
  const [quantities, setQuantities] = useState({})
  const [quoteModalVisible, setQuoteModalVisible] = useState(false)

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true)
      try {
        const token = await getToken()
        const productData = await getProduct(productId, token)
        setProduct(productData)
      } catch (error) {
        console.error("Error fetching product:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchProduct()
  }, [productId, getToken])

  useEffect(() => {
    let isActive = true
    const fetchRelated = async () => {
      try {
        setRelatedLoading(true)
        const token = await getToken()
        const relatedProductss = await getRelatedProducts(productId, token)
        if (!isActive) return
        setRelatedProducts(Array.isArray(relatedProductss) ? relatedProductss : [])
      } catch (error) {
        if (isActive) {
          console.error("Error fetching related products:", error)
        }
      } finally {
        if (isActive) {
          setRelatedLoading(false)
        }
      }
    }

    fetchRelated()
    return () => {
      isActive = false
    }
  }, [productId, getToken])

  const handleQuantityChange = (measure, value) => {
    const sanitizedValue = value && value > 0 ? value : 0
    setQuantities((prev) => ({ ...prev, [measure.medida]: sanitizedValue }))
  }

  const addToCart = (product, medida, quantity) => {
    if (!userLoggedIn) {
      navigate("/signin")
      return
    }

    if (!quantity || quantity <= 0) {
      message.warning("Selecciona una cantidad válida para agregar al carrito")
      return
    }

    // Obtener el carrito del localStorage
    const cart = JSON.parse(localStorage.getItem("cart") || "{}")

    // Verificar si el producto ya existe en el carrito
    if (!cart[productId]) {
      const images = Array.isArray(product.images) ? product.images : []
      cart[productId] = {
        name: product.name,
        images: images,
        measures: [],
      }
    }

    // Buscar si la medida ya existe para este producto
    const existingMeasureIndex = cart[productId].measures.findIndex((item) => item.measure === medida.medida)

    if (existingMeasureIndex !== -1) {
      const existingMeasure = cart[productId].measures[existingMeasureIndex]
      existingMeasure.quantity += quantity
    } else {
      cart[productId].measures.push({
        measure: medida.medida,
        prices: Array.isArray(medida.precios) ? medida.precios : [],
        quantity: quantity,
      })
    }


    // Guardamos el carrito actualizado en el localStorage
    localStorage.setItem("cart", JSON.stringify(cart))

    // Notificamos que el carrito ha sido actualizado
    window.dispatchEvent(new CustomEvent("cartUpdated", { detail: { showDrawer: true } }))
  }

  if (loading) {
    return <Spin size="large" className="loading-spinner" />
  }

  if (!product) {
    return <div>No se encontró el producto</div>
  }

  const supplierName =
    product.supplier_name ||
    product.supplierName ||
    product.supplier_display_name ||
    product.supplierDisplayName ||
    product.supplier_company ||
    product.supplierCompany ||
    ""

  return (
    <div className="product-detail-container">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={13} className="sticky-column">
          <Row>
            <Col xs={24} md={4}>
              <div className="image-thumbnails">
                {(product.images || []).map((image, index) => (
                  <div
                    key={index}
                    className={`thumbnail-wrapper ${selectedImage === index ? "selected" : ""}`}
                    onClick={() => setSelectedImage(index)}
                  >
                    <div
                      className="thumbnail-image"
                      style={{ backgroundImage: `url(${image || "/placeholder.jpg"})` }}
                    />
                  </div>
                ))}
              </div>
            </Col>
            <Col xs={24} md={20}>
              <div className="main-image-wrapper">
                <div
                  className="main-image"
                  style={{ backgroundImage: `url(${(product.images || [])[selectedImage] || "/placeholder.jpg"})` }}
                />
              </div>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={11} className="scrollable-column">
          <div className="measures-prices">
            <Title level={2}>{product.name}</Title>
            {supplierName && (
              <div className="product-supplier">
                <span className="product-supplier-badge">Proveedor</span>
                <span className="product-supplier-name">{supplierName}</span>
              </div>
            )}
            <Title level={4}>Medidas</Title>
            <Collapse>
              {(product.measures || []).map((medida, index) => (
                <Panel
                  className="panell"
                  header={
                    <div className="measure-header">
                      <span>Medida: {medida.medida || `Medida ${index + 1}`}</span>
                    </div>
                  }
                  key={index}
                >
                  <div className="quantity-cost-section">
                    <Row gutter={[16, 16]} align="middle">
                      <Col xs={24} sm={12}>
                        <span>Cantidad:</span>
                        <InputNumber
                          min={1}
                          value={quantities[medida.medida] || 0}
                          onChange={(value) => handleQuantityChange(medida, value)}
                          style={{ width: "100%" }}
                        />
                      </Col>
                      <Col xs={24} sm={12}>
                        <Button
                          onClick={() => addToCart(product, medida, quantities[medida.medida])}
                          icon={<ShoppingCartOutlined />}
                          type="primary"
                          block
                        >
                          Agregar
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {/* 
                    Sistema anterior con precios detallados.
                    Se deja comentado por si en el futuro se reintroducen listas de precios.
                  */}
                  {/* <table className="price-table">
                    ...
                  </table> */}
                </Panel>
              ))}
            </Collapse>

            {/* Botón para solicitar cotizaciones de proveedores similares */}
            {userLoggedIn && user?.role === "cliente" && (
              <>
                <Divider style={{ margin: "24px 0 16px 0" }} />
                <Button
                  type="default"
                  icon={<TeamOutlined />}
                  onClick={() => setQuoteModalVisible(true)}
                  block
                  size="large"
                  style={{
                    height: "48px",
                    borderColor: "#1890ff",
                    color: "#1890ff"
                  }}
                >
                  Solicitar cotizaciones de proveedores similares
                </Button>
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    textAlign: "center",
                    marginTop: 8,
                    fontSize: 12
                  }}
                >
                  Encuentra otros proveedores que venden productos similares
                </Text>
              </>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Tabs defaultActiveKey="1" className="product-tabs">
            <TabPane tab="Descripción" key="1">
              <p>{product.details}</p>
            </TabPane>
            <TabPane tab="Información Adicional" key="2">
              <Descriptions column={1}>
                <Descriptions.Item label="Materiales">{product.materials.join(", ")}</Descriptions.Item>
                <Descriptions.Item label="Certificaciones">{product.certifications.join(", ")}</Descriptions.Item>
                <Descriptions.Item label="Uso">{product.use}</Descriptions.Item>
              </Descriptions>
            </TabPane>
            <TabPane tab="Ficha Técnica" key="3">
              {product.technical_sheet ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    padding: '12px 24px',
                    backgroundColor: '#f0f8ff',
                    border: '2px solid #1890ff',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <span style={{ fontSize: '24px', marginRight: '12px' }}>📄</span>
                    <a
                      href={product.technical_sheet}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="technical-sheet-link"
                      style={{
                        color: '#1890ff',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#40a9ff'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#1890ff'
                      }}
                    >
                      Ver Ficha Técnica
                    </a>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                    Se abrirá en una nueva pestaña
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Text type="secondary">No hay ficha técnica disponible</Text>
                </div>
              )}
            </TabPane>
          </Tabs>
        </Col>
      </Row>
      {/* Related Products Section */}
      {(relatedLoading || (relatedProducts && relatedProducts.length > 0)) && (
        <Row className="related-products-section" style={{ marginTop: "40px" }}>
          <Col span={24}>
            <Title level={3}>Productos Relacionados</Title>
            {relatedLoading ? (
              <Row gutter={[16, 16]}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col xs={24} sm={12} md={6} key={`related-skeleton-${index}`}>
                    <div
                      className="related-product-card"
                      style={{
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                        overflow: "hidden",
                        height: "100%",
                      }}
                    >
                      <div style={{ padding: "12px" }}>
                        <Skeleton.Image
                          active
                          style={{ width: "100%", height: 180, marginBottom: 12 }}
                        />
                        <Skeleton
                          active
                          title={{ width: "80%" }}
                          paragraph={{ rows: 2, width: ["100%", "90%"] }}
                        />
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <Row gutter={[16, 16]}>
                {relatedProducts.slice(0, 4).map((relatedProduct) => (
                  <Col xs={24} sm={12} md={6} key={relatedProduct.id}>
                    <div
                      className="related-product-card"
                      onClick={() => navigate(`/producto/${relatedProduct.id}`)}
                      style={{
                        cursor: "pointer",
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        height: "100%",
                      }}
                    >
                      <div
                        className="related-product-image"
                        style={{
                          height: "180px",
                          backgroundImage: `url(${relatedProduct.images && relatedProduct.images[0] ? relatedProduct.images[0] : "/placeholder.jpg"})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                      <div style={{ padding: "12px" }}>
                        <Text strong style={{ display: "block", marginBottom: "4px" }}>
                          {relatedProduct.name}
                        </Text>
                        <Text type="secondary" ellipsis={{ rows: 2 }}>
                          {relatedProduct.details}
                        </Text>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      )}

      {/* Modal para solicitar cotizaciones */}
      <QuoteRequestModal
        visible={quoteModalVisible}
        onCancel={() => setQuoteModalVisible(false)}
        product={product}
        productId={productId}
        getToken={getToken}
        onSuccess={() => {
          message.success("Solicitud de cotización enviada exitosamente")
          setQuoteModalVisible(false)
        }}
      />
    </div>
  )
}

export default ProductDetail

